import { getJWTToken } from '../../utils/auth';
import type { WidaStudent } from '../Upload/widaHistory';
import type { ReclassCriteria } from '../Reclassification/reclassCriteria';
import {
  currentLevel,
  criterionType,
  criterionWord,
  domainMinimumFor,
  domainTrend,
  isLtel,
  latestAdministrationLevel,
  levelChange,
  levelLabel,
  scoredAdministrations,
  DOMAINS,
  DOMAIN_LABELS,
  type DomainKey,
} from './derive';
import { gradeBand, yearsBucket } from './domainFocus';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type PlanSnapshotDomainSignal = {
  pacingSignal: string;
  proficiencyLevel: string;
  proficiencyScore: number | null;
  yearOverYearChange: number | null;
};

export type PlanSnapshotInputs = {
  compositeProficiencyLevel: string;
  gradeBand: string;
  yearsInElBucket: string;
  isLtelStudent: boolean;
  assessmentFramework: string;
  criterionType: 'exit' | 'review';
  compositeProficiencyScore: number | null;
  compositeYearOverYearChange: number | null;
  scoredAdministrationsCount: number;
  listening: PlanSnapshotDomainSignal;
  speaking: PlanSnapshotDomainSignal;
  reading: PlanSnapshotDomainSignal;
  writing: PlanSnapshotDomainSignal;
};

export type PlanSnapshotContent = {
  capabilities: string[];
  areasForDevelopment: string[];
};

const PENDING_SIGNAL: PlanSnapshotDomainSignal = {
  pacingSignal: 'pending',
  proficiencyLevel: '',
  proficiencyScore: null,
  yearOverYearChange: null,
};

function scoredPacingSignal(
  student: WidaStudent,
  domain: DomainKey,
  level: number,
  criteria: ReclassCriteria,
): string {
  const min = domainMinimumFor(criteria, domain);
  if (min != null) return level >= min ? 'minimum met' : 'below minimum';
  switch (domainTrend(student, domain).category) {
    case 'rising':
      return 'growing';
    case 'falling':
      return 'declining';
    default:
      return 'flat';
  }
}

function domainSignal(
  student: WidaStudent,
  domain: DomainKey,
  criteria: ReclassCriteria,
): PlanSnapshotDomainSignal {
  const level = latestAdministrationLevel(student, domain);
  if (level == null) return { ...PENDING_SIGNAL };
  return {
    pacingSignal: scoredPacingSignal(student, domain, level, criteria),
    proficiencyLevel: levelLabel(level),
    proficiencyScore: level,
    yearOverYearChange: levelChange(student, domain),
  };
}

export function derivePlanSnapshotInputs(
  student: WidaStudent,
  criteria: ReclassCriteria,
): PlanSnapshotInputs {
  const composite = currentLevel(student, 'composite');
  const latestComposite = latestAdministrationLevel(student, 'composite');
  const [listening, speaking, reading, writing] = DOMAINS.map((domain) =>
    domainSignal(student, domain, criteria),
  ) as [
    PlanSnapshotDomainSignal,
    PlanSnapshotDomainSignal,
    PlanSnapshotDomainSignal,
    PlanSnapshotDomainSignal,
  ];
  return {
    compositeProficiencyLevel: composite != null ? levelLabel(composite) : '',
    gradeBand: gradeBand(student.latestGrade ?? ''),
    yearsInElBucket: yearsBucket(student.yearsInEl),
    isLtelStudent: isLtel(student),
    assessmentFramework: criteria.assessment,
    criterionType: criterionType(criteria),
    compositeProficiencyScore: latestComposite,
    compositeYearOverYearChange: latestComposite == null ? null : levelChange(student, 'composite'),
    scoredAdministrationsCount: scoredAdministrations(student, 'any'),
    listening,
    speaking,
    reading,
    writing,
  };
}

const LABEL_TO_DOMAIN: Record<string, DomainKey> = Object.fromEntries(
  DOMAINS.map((domain) => [DOMAIN_LABELS[domain], domain]),
);

function domainScore(student: WidaStudent, domain: DomainKey): string {
  const level = latestAdministrationLevel(student, domain);
  return level != null ? level.toFixed(1) : '';
}

function appendScore(bullet: string, score: string): string {
  if (!score) return bullet;
  const close = bullet.lastIndexOf(')');
  if (close === -1) return bullet;
  return `${bullet.slice(0, close)}, ${score}${bullet.slice(close)}`;
}

function appendMinimumGap(
  bullet: string,
  student: WidaStudent,
  domain: DomainKey,
  criteria: ReclassCriteria,
): string {
  const level = latestAdministrationLevel(student, domain);
  const min = domainMinimumFor(criteria, domain);
  if (level == null || min == null || level >= min) return bullet;
  const gap = (Math.round((min - level) * 10) / 10).toFixed(1);
  const suffix = `; ${gap} below its ${min} ${criterionWord(criteria)}`;
  return bullet.endsWith('.') ? `${bullet.slice(0, -1)}${suffix}.` : `${bullet}${suffix}`;
}

export function appendPlanSnapshotScores(
  content: PlanSnapshotContent,
  student: WidaStudent,
  criteria: ReclassCriteria,
): PlanSnapshotContent {
  return {
    capabilities: content.capabilities.map((bullet, i) => {
      const domain = DOMAINS[i];
      return domain ? appendScore(bullet, domainScore(student, domain)) : bullet;
    }),
    areasForDevelopment: content.areasForDevelopment.map((bullet) => {
      const domain = LABEL_TO_DOMAIN[bullet.split(':')[0]?.trim() ?? ''];
      if (!domain) return bullet;
      return appendMinimumGap(
        appendScore(bullet, domainScore(student, domain)),
        student,
        domain,
        criteria,
      );
    }),
  };
}

export async function fetchPlanSnapshot(
  studentId: string,
  inputs: PlanSnapshotInputs,
): Promise<PlanSnapshotContent> {
  const token = await getJWTToken();
  const response = await fetch(
    `${apiUrl}/students/${encodeURIComponent(studentId)}/plan-snapshot`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(token),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inputs),
    },
  );
  if (!response.ok) {
    throw new Error(`plan-snapshot fetch failed: ${response.status}`);
  }
  const data = (await response.json()) as {
    capabilities?: string[];
    areasForDevelopment?: string[];
  };
  return {
    capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
    areasForDevelopment: Array.isArray(data.areasForDevelopment) ? data.areasForDevelopment : [],
  };
}
